import * as React          from "react";
import ReactTooltip        from 'react-tooltip';

import {FontAwesomeIcon}   from "@fortawesome/react-fontawesome";
import {faFilePdf,
        IconDefinition,
        faBan,
        faPen,
        faMagnifyingGlass} from "@fortawesome/free-solid-svg-icons";

import "./scheduled-route.scss";

import {AnyAction}       from "@Common/react/any-action";
import {cs}              from "@Common/react/chain-services";
import {Load2}           from "@Common/react/load2";
import {
    componentHelper as component,
    ComponentLink,
    makeChain,
    Link,
    valueHelper as value
}                        from "@Common/react/cst2";
import {UseState}        from "@Common/react/use-state";
import {Types}           from "@Common/types";
import {formatDate1}     from "@Common/ui-components/format-date";
import {DataTableD1}     from "@Common/ui-components/heavy/common/design2/data-table/data-table-d1";
import {ellipsisEnd}     from "@Common/ui-components/heavy/common/design2/data-table/mobile/ellipsis-end";
import {Pagination}      from "@Common/ui-components/heavy/common/design2/data-table/pagination";
import {AlertModal}      from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {PageList}        from "@Common/ui-components/heavy/common/design2/pagination-ui/page-list/page-list";
import {PageSize}        from "@Common/ui-components/heavy/common/design2/pagination-ui/page-size/page-size";
import {formatNumberBig} from "@Common/ui-components/language/format-number-big";
import {Responsive}      from "@Common/ui-components/responsive";
import {
    MEDIA_QUERY_MD,
    ViewportTest}        from "@Common/ui-components/viewport-test";
import {sort as sorted}  from "@Common/utils/collections";
import {chain}           from "@Common/utils/fs";

import {consumeContext}  from "@Xpresso/loaders/consume-context";
import {
    AGENT_LEVEL,
    BRANCH_LEVEL,
    COMPANY_LEVEL}       from "@Xpresso/routes/common/logic/user-level";

import {EnlargedView}        from "../../../common/enlarged-view/enlarged-view";
import {CancelAction}        from "../common/cancel-action";
import {OrderDetailsSidebar} from "../common/order-details/order-details-sidebar";
import {OrdersLayout}        from "../common/orders-layout";
import {OrdersSearch}        from "../common/orders-search/orders-search";
import {OrdersHeader}        from "./common/orders-header";
import {OrderCard}           from "./common/order-card";
import {RescheduleModal}     from "./reschedule-modal/reschedule-modal";

type Item = Types.Apis.Payloads.Next.Item.GetScheduled.Item;

export const AccountScheduledRoute = () => (
    makeChain<JSX.Element>()
    .then(({}, next) => OrdersLayout({activeTab: "scheduled", next}))

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("routerHistory"))

    .then("pageSize", ({}, next:ComponentLink<typeof UseState<number>>) => UseState({initValue: 10, next}))

    .then("searchState", ({}, next:ComponentLink<typeof UseState<string>>) => UseState({next, initValue: ""}))
    .then("sortState", component(UseState as typeof UseState<{id: string, ascending: boolean}>)(
        ({}, S) => S({initValue: {id: "date", ascending: false}})
    ))
    .then("scheduledItems", ({apis}, next:ComponentLink<typeof Load2<Types.Apis.Payloads.Next.Item.GetScheduled.Response>>) => (
        Load2({
            fetch: () => apis.next.item.getScheduled(),
            next,
        })
    ))
    .then("scheduledOrdersState", component(ScheduledOrdersState)(
        ({
            searchState: {value: searchQuery},
            sortState: {value: sortMode}
        }, S) => S({searchQuery, sortMode})
    ))
    .then("cancelAction", ({scheduledOrdersState}, next:ComponentLink<typeof CancelAction>) => CancelAction({onCancelled: scheduledOrdersState.reload, next}))
    .then("orderDetailsSidebar", ({}, next:ComponentLink<typeof OrderDetailsSidebar>) => OrderDetailsSidebar({next}))
    .then("rescheduleModal", ({apis, scheduledOrdersState}, next:ComponentLink<typeof RescheduleModal>) => RescheduleModal({
        onClick: async ({date, params}) => {
            const response = await apis.next.item.updateScheduledDate({
                scheduledDate: date,
                itemId: params.itemId,
            });
            if( response.result === "failure")
            {
                alert("Error: " + response.message);
            }
            else
            {
                scheduledOrdersState.reload();
            }
        },
        next,
    }))
    .then("mapItemAsActions", value(
        ({orderDetailsSidebar, rescheduleModal}) => makeItemAsActionsMap({orderDetailsSidebar, rescheduleModal})
    ))
    .then("responsive", (_, next:ComponentLink<typeof Responsive>) => (
        <Responsive {...{
            sizes: {
                full: 1448,
            },
            next,
        }}/>
    ))
    .then("pageSizeState", ({}, next:ComponentLink<typeof UseState<number>>) => (
        UseState({initValue: 10, next})
    ))
    .then("pagination", ({pageSizeState: {value: pageSize}, scheduledOrdersState}, next:ComponentLink<typeof Pagination<Item>>) => (
        Pagination({next, pageSize, rows: scheduledOrdersState.rows})
    ))
    .then(({company, pageSize, scheduledItems, scheduledOrdersState, userLevels, orderDetailsSidebar, sortState, pageSizeState, pagination, rescheduleModal, mapItemAsActions, cancelAction, responsive, searchState, routerHistory}) => (
        <div className="scheduled-route-8d5">
            {OrdersHeader({
                search: OrdersSearch({
                    searchState,
                    inputTip: (
                        userLevels.userLevel === COMPANY_LEVEL ? (
                            "Search by Order Name, Order ID, Office Name or User Name"
                        ) : userLevels.userLevel === BRANCH_LEVEL ? (
                            "Search by Order Name, Order ID, or User Name"
                        ) : (
                            "Search by Order Name or Order ID"
                        )
                    ) + (userLevels.internalUser ? " (Internal: idBranch or idAgent)" : "")
                }),
                sortState,
            })}
            {scheduledItems.loading ? (
                <div className="loading" xd-qa-type="label" xd-qa-name="Loading">
                    Loading scheduled orders...
                </div>
            ) : (pagination.state.value === -1 && searchState.value) ? (
                <div className="no-scheduled-orders">
                    <p>No results for your query</p>
                    <button className="to-home" xd-qa-type="button" xd-qa-name="Back to the Home Page"
                        onClick={() => {
                            routerHistory.push(`/`);
                        }}
                    >
                        Back to the Home Page
                    </button>
                </div>
            ) : pagination.state.value === -1 ? (
                <div className="no-scheduled-orders">
                    <p>There are no scheduled orders</p>
                    <button className="to-home" xd-qa-type="button" xd-qa-name="Back to the Home Page"
                        onClick={() => {
                            routerHistory.push(`/`);
                        }}
                    >
                        Back to the Home Page
                    </button>
                </div>
            ) : (
                <div className="table">
                    <ViewportTest if={MEDIA_QUERY_MD} then={() => (
                        makeChain<React.ReactNode>()
                        .then(...consumeContext("apis"))
                        .then("state", ({}, next:ComponentLink<typeof UseState<{expandedItem?: Item, dropdownPosition?: {right: number, top: number}}>>) => (
                            UseState({next, initValue: {}})
                        ))
                        .then(({apis, state: {onChange: setState, value: {dropdownPosition, expandedItem}}}) => <>
                            {expandedItem && (
                                <MobileActions
                                    actions={mapItemAsActions({item: expandedItem})}
                                    position={dropdownPosition}
                                    onClose={() => setState({})}
                                    onCancel={() => cancelAction.show({item: expandedItem})}
                                />
                            )}
                            {pagination.getDisplayedRows().map((item) => (
                                OrderCard({
                                    agentName      : item.agent.agentName,
                                    branchName     : item.branch.branchName,
                                    orderName      : item.itemName,
                                    orderId        : item.itemId,
                                    orderDate      : item.orderDate.date,
                                    schFulfillment : item.scheduledDate.date,
                                    expShipDate    : item.shipDate.date,
                                    type           : item.idProgram,
                                    onOptionsClick : ({currentTarget: {clientWidth, offsetLeft, offsetParent, offsetTop}}) => (
                                        // timeout to queue after MobileActions close
                                        window.setTimeout(() => setState({
                                            expandedItem     : item,
                                            dropdownPosition : {right: offsetParent.clientWidth - offsetLeft - clientWidth, top: offsetTop}
                                        }), 0)
                                    ),
                                    orderDetails   : () => orderDetailsSidebar.show({orderDetails: {item, payments: []}}),
                                })
                            ))}
                            {<div className="footer">
                                {scheduledOrdersState.rows.length > 10 ? (
                                    PageSize({state: pageSizeState, xdQaNamePrefix: "Scheduled Orders", pageSizeMessage: ""})
                                ) : (
                                    <div className="spacer"/>
                                )}
                                {pagination.active && PageList(pagination)}
                                <div className="page-summary">
                                    <div className="page-rows-list">
                                        {getPageSummary({
                                            pageIndex: pagination.state.value,
                                            pageSize: pageSizeState.value,
                                            count: scheduledOrdersState.rows.length
                                        })}
                                    </div>
                                </div>
                            </div>}
                        </>)
                        .run()
                    )} else={() => (<>
                        {DataTableD1({
                            paginationKey: "scheduled",
                            pageSize: pageSize.value,
                            rows: scheduledOrdersState.rows.map((r) => ({
                                xdQaName: r.itemId,
                                columns: [
                                    {
                                        render: () => cs(
                                            ["view", (_, next) => UseState({
                                                initValue: {enlarged: false, key: 0},
                                                next,
                                            })],
                                            ["enlargedView", ({view}, next) => EnlargedView({
                                                images: r?.proofUrls?.jpg || [{url: r.thumbnail}],
                                                onChangeView: (key) => view.onChange({...view.value, key: key}),
                                                viewing: view.value.key,
                                                next,
                                            })],
                                            ({enlargedView}) => <div className="order-flex">
                                                <ReactTooltip  id="sch_name" place="bottom" delayShow={100}/>
                                                <div className="thumbnail" xd-qa-type="icon" xd-qa-name="Preview">
                                                    {r.thumbnail ? (
                                                        <img src={r.thumbnail} alt={r.itemName} onClick={() => enlargedView.show()}/>
                                                    ) : (
                                                        <img src={require("../../../../../public/assets/NoImageAvailable.png")} alt="No Image Available"/>
                                                    )}
                                                </div>
                                                <div className="name" xd-qa-type="text" xd-qa-name={r.itemName} data-for="sch_name" data-tip={r.itemName}>
                                                    {responsive.ge("full") ? (
                                                        ellipsisEnd(r.itemName, 30, 2)
                                                    ) : (
                                                        ellipsisEnd(r.itemName, 10, 2)
                                                    )}
                                                </div>
                                            </div>
                                        ),
                                        sortValue: () => r.itemName.trim().toUpperCase(),
                                    },
                                    {
                                        render: () => (
                                            <div className="link" xd-qa-type="link" xd-qa-name="Order Details" onClick={() => orderDetailsSidebar.show({orderDetails: {item:r, payments: []}})}>{r.itemId}</div>
                                        ),
                                        sortValue: () => r.itemId,
                                        domAttrs: {
                                            "xd-qa-type": "text",
                                            "xd-qa-name": "Order ID",
                                        },
                                    },
                                    {
                                        render: () => (r.programType ? r.programType : r.idProgram ?? '').toUpperCase(),
                                        sortValue: () => (r.programType ? r.programType : r.idProgram ?? '').toUpperCase().trim(),
                                        domAttrs: {
                                            "xd-qa-type": "text",
                                            "xd-qa-name": "Type",
                                        },
                                    },
                                    (userLevels?.userLevel < 4) && {
                                        render: () => (<>
                                            <ReactTooltip  id="sch_branch" place="bottom" delayShow={100}/>
                                            {responsive.ge("full") ? (
                                                ellipsisEnd(r.branch.branchName, 30, 2)
                                            ) : (
                                                ellipsisEnd(r.branch.branchName, 10, 2)
                                            )}
                                        </>),
                                        sortValue: () => r.branch.branchName.trim().toUpperCase(),
                                        domAttrs: {
                                            "xd-qa-type": "text",
                                            "xd-qa-name": r.branch.branchName,
                                            "data-tip": r.branch.branchName,
                                            "data-for": "sch_branch"
                                        },
                                    },
                                    (userLevels?.userLevel < 5) && {
                                        render: () => (<>
                                            <ReactTooltip  id="sch_agent" place="bottom" delayShow={100}/>
                                            {responsive.ge("full") ? (
                                                ellipsisEnd(r.agent.agentName, 30, 2)
                                            ) : (
                                                ellipsisEnd(r.agent.agentName, 10, 2)
                                            )}
                                        </>),
                                        sortValue: () => r.agent.agentName.trim().toUpperCase(),
                                        domAttrs: {
                                            "xd-qa-type": "text",
                                            "xd-qa-name": r.agent.agentName,
                                            "data-tip": r.agent.agentName,
                                            "data-for": "sch_agent"
                                        },
                                    },
                                    {
                                        render: () => formatDate1(new Date(r.orderDate.date)),
                                        sortValue: () => new Date(r.orderDate.date).getTime(),
                                        domAttrs: {
                                            "xd-qa-type": "text",
                                            "xd-qa-name": "Date",
                                        },
                                    },
                                    {
                                        render: () => formatDate1(new Date(r.scheduledDate.date)),
                                        sortValue: () => new Date(r.scheduledDate.date).getTime(),
                                        domAttrs: {
                                            "xd-qa-type": "text",
                                            "xd-qa-name": "Sch Fulfillment",
                                        },
                                    },
                                    {
                                        render: () => r.shipDate?.date ? formatDate1(new Date(r.shipDate.date)) : "N/A",
                                        sortValue: () => r.shipDate?.date ? new Date(r.shipDate.date).getTime() : 0,
                                        domAttrs: {
                                            "xd-qa-type": "text",
                                            "xd-qa-name": "Exp Ship Date",
                                        },
                                    },
                                    {
                                        render: () => (
                                            <div className="action">
                                                <ReactTooltip id="sch_icon" place="bottom" delayShow={100}/>
                                                {r.selections.options.find(v => v.name === "PDF Download")?.value && company?.prefs.allowPdfPurchase && (
                                                    <a
                                                        className="fa-layers fa-fw"
                                                        xd-qa-type="icon"
                                                        xd-qa-name="Preview PDF"
                                                        data-for='sch_icon'
                                                        data-tip="Preview PDF"
                                                        href={"/next/web_services/get_production_pdf.php?id_order=" + r.itemId}
                                                    >
                                                        <FontAwesomeIcon key={r.itemId + "faFilePdf"} className="icon5" icon={faFilePdf}/>
                                                    </a>
                                                )}
                                                <div
                                                    className="fa-layers fa-fw"
                                                    xd-qa-type="icon"
                                                    xd-qa-name="Edit Date"
                                                    data-for='sch_icon'
                                                    data-tip="Edit Fulfillment Date"
                                                    onClick={() => rescheduleModal.show({itemId: r.itemId, currentDate: r.scheduledDate.date, orderDate: r.orderDate.date})}
                                                >
                                                    <i className="icon4 fa fa-calendar" xd-qa-type="icon" xd-qa-name="calendar" />
                                                </div>
                                                <div className="fa-layers fa-fw"
                                                    xd-qa-type="icon"
                                                    xd-qa-name="Trash"
                                                    data-for='sch_icon'
                                                    data-tip="Cancel Order"
                                                    onClick={() => cancelAction.show({item: r})}
                                                >
                                                    <img className="icon1"
                                                        src={require("../../../../../public/assets/trash2.svg")}
                                                        alt="delete"
                                                    />
                                                </div>
                                            </div>
                                        ),
                                        domAttrs: {
                                            "xd-qa-type": "icon",
                                            "xd-qa-name": "Actions",
                                        },
                                    },
                                ].filter(v => v),
                            })),
                            columns: columns({userLevel: userLevels?.userLevel, responsive}),
                            emptyAction: {
                                label: "No orders found"
                            },
                            getFooter: ({colCount, pagination}) => ({
                                columns: [{
                                    colSpan: colCount,
                                    render: () => (
                                        <div className="controls">
                                            {PageSize({state: pageSize})}
                                            {pagination.active && PageList(pagination)}
                                            <div className="page-summary">
                                                <div className="page-rows-list">
                                                    {getPageSummary({
                                                        pageIndex: pagination.state.value,
                                                        pageSize: pageSize.value,
                                                        count: scheduledOrdersState.rows.length
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }],
                            }),
                        })}
                    </>)}/>
                </div>
            )}
        </div>
    ))
    .run()
);

const getPageSummary = ({pageIndex, pageSize, count}) => {
    return (
        formatNumberBig(pageIndex * pageSize + 1) +
        "-" +
        formatNumberBig(
            Math.min(count, (pageIndex + 1) * pageSize)
        ) +
        " of " + count

    );
};

const columns = ({userLevel, responsive}) => [
    {
        label: "ORDER NAME",
        className: "order-name",
        width: userLevel === 5 ? (
            "860px"
        ) : userLevel === 4 ? (
            "660px"
        ) : (
            "470px"
        ),
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "ORDER NAME",
        },
    },
    {
        label: "ORDER ID",
        className: "order-id",
        width: "70px",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "link",
            "xd-qa-name": "ORDER ID",
        },
    },
    {
        label: "TYPE",
        className: "type",
        width: "100px",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "TYPE",
        },
    },
    (userLevel < 4) && {
        label: "OFFICE",
        className: "office",
        width: "200px",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "OFFICE",
        },
    },
    (userLevel < 5) && {
        label: "USER",
        className: "user",
        width: "200px",
        sortable: {
            defaultDirection: "asc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "USER",
        },
    },
    {
        label: "DATE",
        className: "date",
        width: "110px",
        sortable: {
            defaultDirection: "desc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "DATE",
        },
    },
    {
        label: responsive.ge("full") ? ("SCH FULFILLMENT") : ("SCH DATE"),
        className: "date",
        width: "110px",
        sortable: {
            defaultDirection: "desc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "SCH FULFILLMENT",
        },
    },
    {
        label: responsive.ge("full") ? ("EXP SHIP DATE") : ("SHIP DATE"),
        className: "date",
        width: "110px",
        sortable: {
            defaultDirection: "desc",
        },
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "EXP SHIP DATE",
        },
    },
    {
        label: "ACTIONS",
        className: responsive.ge("full") ? ("delete") : ("delete small"),
        width: responsive.ge("full") ? ("140px") : ("90px"),
        domAttrs: {
            "xd-qa-type": "label",
            "xd-qa-name": "ACTIONS",
        },
    },
].filter(v => v);

const makeOrderFilter = (search:string, userLevels: Types.Apis.Loaders.LoadUserLevelsNext) => {
    const searchLC = search.toLowerCase();
    return (order) => [
        order.itemId,
        order.itemName,
        ...userLevels.userLevel != AGENT_LEVEL ? [order.agent.agentName] : [],
        ...userLevels.userLevel == COMPANY_LEVEL ? [order.branch.branchName] : [],
        ...(userLevels.internalUser ? [
            order.idBranch,
            order.idAgent
        ] : []),
    ].filter(v=>v).some(val => String(val).toLowerCase().includes(searchLC))
};


interface ItemAction {
    icon: IconDefinition,
    label: string,
    name: string,
    run: () => Promise<void>,
}

const MobileActions = ({actions, onClose, onCancel, position}:{
    actions  : ItemAction[],
    onClose  : () => void,
    onCancel : () => void,
    position : {right: number, top: number},
}) => (
    <div className="mobile-dropdown" style={position}>
        <AnyAction action={onClose}/>
        {actions.map(action => (
            <a
                key={action.name}
                className="dropdown-item"
                xd-qa-type="dropdown_item"
                xd-qa-name={action.label}
                onClick={() => action.run()}
            >
                <i className="icon">{action.icon && <FontAwesomeIcon icon={action.icon}/>}</i>
                {action.label}
            </a>
        ))}
        <a
            key="cancel"
            className="cancel dropdown-item"
            xd-qa-type="dropdown_item"
            xd-qa-name="Cancel"
            onClick={onCancel}
        >
            <i className="icon"><FontAwesomeIcon icon={faBan}/></i>
            Cancel Order
        </a>
    </div>
);

const makeItemAsActionsMap = ({orderDetailsSidebar, rescheduleModal}) => (
    ({item}:{item: Item}) => {
        const actions:Array<ItemAction> = [];
            actions.push({
                name  : "preview-pdf",
                label : "Preview PDF",
                icon  : faFilePdf,
                run   : async () => {window.open(`/next/web_services/get_production_pdf.php?id_order=${item.itemId}`, "_blank")},
            });

            actions.push({
                name  : "order-details",
                label : "Order Details",
                icon  : faMagnifyingGlass,
                run   : async () => orderDetailsSidebar.show({orderDetails: {item, payments: []}}),
            });

            actions.push({
                name  : "edit-date",
                label : "Edit Date",
                icon  : faPen,
                run   : async () => rescheduleModal.show({itemId: item.itemId, currentDate: item.scheduledDate.date}),
            });

        return actions;
    }
);

const ScheduledOrdersState = ({next: rootNext, searchQuery, sortMode}:{
    next        : Link<JSX.Element, {
        error          : boolean,
        loading        : boolean,
        onItemCanceled : (obj:{
            itemId: string,
        }) => void,
        reload         : () => void,
        rows           : Types.Apis.Payloads.Next.Item.GetScheduled.Item[],
    }>,
    searchQuery : string,
    sortMode    : {id: string, ascending: boolean},
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("apis"))
    .then(...consumeContext("cart"))
    .then(...consumeContext("menus"))
    .then("alertModal", component(AlertModal)(({}, D) => D({})))
    .then("scheduledItems", ({apis}, next:ComponentLink<typeof Load2<Types.Apis.Payloads.Next.Item.GetScheduled.Response>>) => (
        Load2({
            fetch: () => apis.next.item.getScheduled(),
            next,
        })
    ))
    .then("filteredItems", value(
        ({scheduledItems, userLevels}) => scheduledItems.value?.filter?.(makeOrderFilter(searchQuery, userLevels))
    ))
    .then(({alertModal: {show: alert}, filteredItems, scheduledItems: {change: changeScheduledItems, loading, reload}}) => (
        rootNext({
            loading,
            reload,
            error          : !filteredItems,
            onItemCanceled : async ({itemId}) => {
                alert({message: "Item canceled"});
                changeScheduledItems((items) => items.filter((row) => row.itemId !== itemId))
            },
            rows          : chain(
                sorted(filteredItems ?? [], (item) => {
                    let sortValue:any;
                    const DEFAULT_SORT_MODE = item.orderDate.date;
                    const SORT_MODES = {
                        agentName   : item.agent.agentName.trim().toUpperCase(),
                        branchName  : item.branch.branchName.trim().toUpperCase(),
                        orderName   : item.itemName.trim().toUpperCase(),
                        type        : item.idProgram,
                        date        : item.orderDate.date,
                        schDate     : item.scheduledDate.date,
                        expShipDate : item.shipDate,
                        itemId      : item.itemId,
                    }

                    sortValue = SORT_MODES?.[sortMode.id] ?? DEFAULT_SORT_MODE;

                    // always sort falsies last
                    return (sortValue || (sortMode.ascending ? String.fromCharCode(65535) : "")).toString();
                }),
                (sortedItems:any[]) => sortMode.ascending ? sortedItems : sortedItems.reverse()
            ),
        })
    ))
    .run()
);
